/**
 * The active tab kept in view (D-215): on a narrow screen a row of tabs scrolls sideways
 * (`responsive.css`), and an address that opens a later tab — a link to a job's schedule of
 * values, the live meeting's stepper moving on — would leave the one that is open off the edge.
 * When a strip's active tab changes, the strip scrolls just far enough to show it; a strip the
 * person scrolled by hand is left alone until its active tab changes again.
 */

/** The strip's new scrollLeft to show the tab whole, or null when it already shows. Positions are in the strip's own scroll coordinates. */
export function scrollToShow(scrollLeft: number, width: number, tabLeft: number, tabWidth: number, margin = 16): number | null {
  if (tabLeft < scrollLeft) return Math.max(0, tabLeft - margin);
  if (tabLeft + tabWidth > scrollLeft + width) return Math.max(0, tabLeft + tabWidth - width + margin);
  return null;
}

/** Watches `root` for tab strips whose active tab changed and brings it into view. Returns the stop. */
export function keepActiveTabsInView(root: HTMLElement): () => void {
  const shown = new WeakMap<Element, Element>();
  let frame = 0;
  const sweep = () => {
    frame = 0;
    for (const strip of root.querySelectorAll<HTMLElement>('.tabs')) {
      const active = strip.querySelector<HTMLElement>('.tab--active');
      if (!active || shown.get(strip) === active) continue;
      shown.set(strip, active);
      if (strip.scrollWidth <= strip.clientWidth) continue;
      const left = active.getBoundingClientRect().left - strip.getBoundingClientRect().left + strip.scrollLeft;
      const next = scrollToShow(strip.scrollLeft, strip.clientWidth, left, active.offsetWidth);
      if (next !== null) strip.scrollLeft = next;
    }
  };
  const schedule = () => {
    if (frame === 0) frame = requestAnimationFrame(sweep);
  };
  const observer = new MutationObserver(schedule);
  observer.observe(root, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
  schedule();
  return () => {
    observer.disconnect();
    if (frame !== 0) cancelAnimationFrame(frame);
  };
}
